/*
 * Copyright 2026 North Carolina State University
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Autocomplete, Chip, TextField } from '@mui/material';

const idOf = (t) => String(t?._id || t);

/**
 * Pick any number of compartment types. `value` may hold ids or populated
 * type objects (as the API returns them); `onChange` always receives ids.
 */
const CompartmentTypeSelect = ({
  value = [], onChange, label = 'Compartment Types', helperText, margin = 'normal', disabled = false
}) => {
  const [types, setTypes] = useState([]);

  useEffect(() => {
    axios.get('/api/compartment-types')
      .then(res => setTypes(res.data))
      .catch(err => console.error('Error loading compartment types:', err));
  }, []);

  const selectedIds = (value || []).map(idOf);
  const selected = types.filter(t => selectedIds.includes(t._id));

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      options={types}
      value={selected}
      getOptionLabel={(t) => t.name}
      isOptionEqualToValue={(a, b) => a._id === b._id}
      onChange={(_, picked) => onChange(picked.map(t => t._id))}
      renderTags={(picked, getTagProps) => picked.map((t, index) => (
        <Chip size="small" label={t.name} {...getTagProps({ index })} key={t._id} />
      ))}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          margin={margin}
          placeholder={selected.length ? '' : 'Optional'}
          helperText={types.length === 0
            ? 'No compartment types yet — add them under Manage Program Data → Compartment Types.'
            : helperText}
        />
      )}
    />
  );
};

export default CompartmentTypeSelect;
